/**
 * Institution Transformer
 *
 * Transforms backend institution data to workflow format
 * Extracted from workflow-data.service.ts for better separation of concerns
 */

import type { WorkflowInstitution, InstitutionForm } from '../workflow-types';

interface InstitutionStage {
  stage: string;
  completionState?: string;
}

interface RawInstitution {
  id?: number;
  type?: { isNotifiable?: boolean } | string | null;
  stages?: InstitutionStage[];
  email?: string | null;
  assetCollectionEmail?: string | null;
  postalAddress?: string | null;
  hardCopyRequired?: boolean;
  [key: string]: unknown;
}

interface RawMatterInstitution {
  institution?: RawInstitution;
  lastNotificationDate?: string | null;
  estateReference?: string | null;
  [key: string]: unknown;
}

interface RawInventoryItem {
  institutionId?: number;
  [key: string]: unknown;
}

/**
 * Transform backend institution data to workflow format
 * Adds workflow-specific fields like notification and collection status
 */
export function transformInstitution(
  matterInstitution: unknown,
  inventory: readonly unknown[] = [],
  forms: InstitutionForm[] = []
): WorkflowInstitution {
  // Extract the actual institution from the matter-institution relationship
  const wrapper = (matterInstitution ?? {}) as RawMatterInstitution;
  const institution: RawInstitution =
    wrapper.institution || (matterInstitution as RawInstitution);

  // Check if this institution type is notifiable
  const typeObj =
    typeof institution.type === 'object' && institution.type !== null
      ? institution.type
      : null;
  const isNotifiable = typeObj?.isNotifiable ?? true;

  const stages = institution.stages ?? [];
  const discoveryStage = stages.find((s) => s.stage === 'discovery');
  const collectionStage = stages.find((s) => s.stage === 'collection');

  return {
    ...(institution as Record<string, unknown>),
    canBeNotified: isNotifiable,
    notificationStatus:
      discoveryStage?.completionState === 'complete'
        ? 'sent'
        : discoveryStage?.completionState === 'in_progress'
          ? 'draft'
          : 'pending',
    collectionStatus:
      collectionStage?.completionState === 'complete'
        ? 'collected'
        : collectionStage?.completionState === 'in_progress'
          ? 'sent'
          : 'pending',
    lastNotificationDate: wrapper.lastNotificationDate ?? null,
    estateReference: wrapper.estateReference ?? null,
    emailAddress: institution.email ?? institution.assetCollectionEmail ?? null,
    mailingAddress: institution.postalAddress ?? null,
    requiresMail: institution.hardCopyRequired ?? false,
    inventory: (inventory as RawInventoryItem[]).filter(
      (item) => item.institutionId === institution.id
    ),
    forms: forms
  } as unknown as WorkflowInstitution;
}
